// studioBox — the container tool's drag box (pen B4): which cards it holds
// and in what order they become a module's parts. Pure, in canvas units.
//
// Only a card fully inside the box counts (a box partly over a card leaves
// it out). Reading order is rows top to bottom, then left to right: cards
// whose tops are within a row's tolerance of the row's first card share it,
// so a slightly uneven row still reads left to right.

export { boxBetween, cardsInBox, readingOrder };

import type { Placement, Point } from "./studioLayout.js";

type Box = {
    readonly x: number;
    readonly y: number;
    readonly width: number;
    readonly height: number;
};

// Tops closer than this share a row (half a card's height).
const ROW_TOLERANCE = 64;

const boxBetween = (a: Point, b: Point): Box => ({
    x: Math.min(a.x, b.x),
    y: Math.min(a.y, b.y),
    width: Math.abs(a.x - b.x),
    height: Math.abs(a.y - b.y),
});

const fullyInside = (box: Box, p: Placement): boolean =>
    p.x >= box.x &&
    p.y >= box.y &&
    p.x + p.width <= box.x + box.width &&
    p.y + p.height <= box.y + box.height;

// `ids` (each with a placement) in reading order; an id without one is left
// out.
const readingOrder = (
    ids: readonly string[],
    placements: ReadonlyMap<string, Placement>,
): readonly string[] => {
    const placed = ids
        .flatMap((id) => {
            const p = placements.get(id);
            return p === undefined ? [] : [{ id, p }];
        })
        .sort((a, b) => a.p.y - b.p.y || a.p.x - b.p.x);
    const rows = placed.reduce<(typeof placed)[]>((acc, card) => {
        const row = acc.at(-1);
        const top = row?.[0]?.p.y;
        return row !== undefined &&
            top !== undefined &&
            card.p.y - top < ROW_TOLERANCE
            ? [...acc.slice(0, -1), [...row, card]]
            : [...acc, [card]];
    }, []);
    return rows.flatMap((row) =>
        [...row].sort((a, b) => a.p.x - b.p.x).map(({ id }) => id),
    );
};

const cardsInBox = (
    box: Box,
    ids: readonly string[],
    placements: ReadonlyMap<string, Placement>,
): readonly string[] =>
    readingOrder(
        ids.filter((id) => {
            const p = placements.get(id);
            return p !== undefined && fullyInside(box, p);
        }),
        placements,
    );
